import type { GapAnalysisSummary } from '~/features/catalog-operations/types/catalog-operations.types';
import { cn } from '~/utils/cn';

import type { AspectRollup } from './gap-analysis.utils';
import { formatCount } from './gap-analysis.utils';

interface Props {
  summary: GapAnalysisSummary;
  rollups: AspectRollup[];
}

/** One before→after figure with a proportion bar underneath. `from` is null for
 *  figures that have no baseline to compare against (search demand). */
function Stat({
  label,
  from,
  to,
  hint,
  bars,
  barTotal,
  testId,
}: {
  label: string;
  from: number | null;
  to: number;
  hint: string;
  bars?: { kind: 'was' | 'added'; value: number }[];
  barTotal?: number;
  /** Tile `data-testid`; values get `${testId}-from-value` / `${testId}-value`. */
  testId: string;
}) {
  const total = barTotal && barTotal > 0 ? barTotal : 1;

  return (
    <div
      className="flex flex-col gap-2 border-b p-4 last:border-r-0 md:border-r"
      data-testid={testId}
    >
      <span className="text-muted-foreground text-[10.5px] font-semibold tracking-wider uppercase">
        {label}
      </span>
      <span className="flex items-baseline gap-2 tabular-nums">
        {from !== null ? (
          <>
            <span
              className="text-muted-foreground text-lg font-medium"
              data-testid={`${testId}-from-value`}
            >
              {formatCount(from)}
            </span>
            <span className="text-muted-foreground text-xs" aria-hidden="true">
              →
            </span>
          </>
        ) : null}
        <span
          className="text-success text-[27px] leading-none font-semibold tracking-tight"
          data-testid={`${testId}-value`}
        >
          {formatCount(to)}
        </span>
      </span>
      <span className="text-muted-foreground text-xs" data-testid={`${testId}-hint`}>
        {hint}
      </span>
      {bars ? (
        <div className="flex h-[5px] gap-0.5" aria-hidden="true">
          {bars
            .filter((bar) => bar.value > 0)
            .map((bar) => (
              <span
                key={bar.kind}
                className={cn(
                  'block rounded-sm',
                  bar.kind === 'was' ? 'bg-muted-foreground/50' : 'bg-success',
                )}
                style={{ width: `${String((bar.value / total) * 100)}%` }}
              />
            ))}
        </div>
      ) : null}
    </div>
  );
}

/** Names the categories that gained the most, so a headline figure points at
 *  real content instead of restating itself ("all of them are new"). */
function namedCategories(rollups: AspectRollup[], limit: number): string {
  const named = [...rollups]
    .filter((rollup) => rollup.added.length > 0)
    .sort((a, b) => b.added.length - a.added.length || a.aspect.localeCompare(b.aspect));

  // Capitalised here rather than with a `capitalize` class, which would also
  // title-case the connecting words in the joined sentence.
  const shown = named
    .slice(0, limit)
    .map((rollup) => rollup.aspect.charAt(0).toUpperCase() + rollup.aspect.slice(1));
  const remaining = named.length - shown.length;

  if (shown.length === 0) return '';
  return remaining > 0 ? `${shown.join(', ')} and ${String(remaining)} more` : shown.join(', ');
}

/**
 * Headline figures for the gap tab. Each is a before→after pair rather than a
 * standalone number, so the size of the change is the thing being reported.
 */
export function GapSummaryStats({ summary, rollups }: Props) {
  const aspectsCovered = rollups.filter((rollup) => rollup.had.length > 0).length;
  const emptyAspects = rollups.length - aspectsCovered;
  const showDemand = summary.netNewSearchVolume > 0;
  const topCategories = namedCategories(rollups, 3);
  const filledCategories = namedCategories(rollups, 4);

  return (
    // Column count tracks the number of tiles actually rendered — a fixed
    // three-column grid leaves a dead third of the row when demand is absent.
    <div
      className={cn('grid grid-cols-1 sm:grid-cols-2', showDemand && 'lg:grid-cols-3')}
      data-testid="gap-analysis-summary-stats"
    >
      <Stat
        testId="gap-analysis-stat-attributes-card"
        label="Attributes"
        from={summary.baselineAttributeCount}
        to={summary.enrichedAttributeCount}
        // Points at what was actually added rather than restating the figure
        // above it — "all of them are new" tells the reader nothing they can use.
        hint={
          summary.restatedCount > 0
            ? `${formatCount(summary.netNewCount)} new, ${formatCount(summary.restatedCount)} restated`
            : topCategories
              ? `${formatCount(summary.netNewCount)} new — mostly ${topCategories}`
              : `${formatCount(summary.netNewCount)} new`
        }
        bars={[
          { kind: 'was', value: summary.baselineAttributeCount },
          { kind: 'added', value: summary.netNewCount },
        ]}
        barTotal={summary.enrichedAttributeCount}
      />
      <Stat
        testId="gap-analysis-stat-categories-card"
        label="Categories covered"
        from={aspectsCovered}
        to={rollups.length}
        hint={
          emptyAspects === rollups.length
            ? `All new: ${filledCategories}`
            : emptyAspects > 0
              ? `${String(emptyAspects)} were empty: ${filledCategories}`
              : 'All were already covered before'
        }
        bars={[
          { kind: 'was', value: aspectsCovered },
          { kind: 'added', value: emptyAspects },
        ]}
        barTotal={rollups.length}
      />
      {showDemand ? (
        <Stat
          testId="gap-analysis-stat-search-volume-card"
          label="New monthly search volume"
          from={null}
          to={summary.netNewSearchVolume}
          hint="combined searches for the new attributes"
        />
      ) : null}
    </div>
  );
}
